import { Component, OnInit, inject, Signal } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Store } from '@ngxs/store';
import { BreakpointObserver } from '@angular/cdk/layout';
import {
    Observable,
    distinctUntilChanged,
    switchMap,
    of,
    filter,
    map,
    combineLatest,
    startWith,
    pairwise,
    debounceTime,
} from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { AuthState, Logout } from '../../states/auth.state';

import { SidenavService } from '../../../shared/services/sidenav.service';
import { Router } from '@angular/router';
import {
    OrganizationState,
    StudyType,
} from '../../../features/admin/organization/organization.state';
import { Organization } from '../../models/organization.model';
import { OrganizationService } from '../../../features/admin/organization/organization.service';
import { ConcessionControlStatus } from 'src/app/features/main/concessioncontrol/assets/constants';
import { User } from '../../models/user.model';
import { CracMapStatus } from 'src/app/features/main/cracmap/assets/constants';

export interface OptionList {
    label: string;
    value: string;
}

@UntilDestroy()
@Component({
    selector: 'app-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss'],
})
export class HeaderComponent {
    public isCollapsed: boolean = true;
    public headerFormGroup: FormGroup = this.formBuilder.group({
        organizationId: ['', Validators.required],
        studyType: ['', Validators.required],
        year: ['', Validators.required],
    });

    public selectOptions = [
        {
            label: 'Contrôle de concession',
            value: StudyType.CC,
        },
        {
            label: 'Cartes CRAC',
            value: StudyType.CRACMAP,
        } /*
        {
            label: 'Benchmark CRAC',
            value: StudyType.CRAC,
        },*/,
    ];

    public loggedUser$: Observable<User> = inject(Store)
        .select(AuthState.loggedUser)
        .pipe(filter(Boolean));
    public organizations$: Observable<Organization[]> = inject(Store).select(
        OrganizationState.organizations,
    );

    public currentOrganization$: Observable<Organization | null> = inject(Store).select(
        OrganizationState.currentOrganization,
    );
    public currentYear$: Observable<number | null> = inject(Store).select(
        OrganizationState.currentYear,
    );

    public currentStudyType$: Observable<StudyType | null> = inject(Store).select(
        OrganizationState.currentStudyType,
    );

    public initials: string = '';
    public avatarSrc: string | null = null;

    public isSmallScreen!: boolean;

    public yearOptions: OptionList[] = [];
    public organizationOptions: OptionList[] = [];

    public displayAdministration: boolean = false;
    public displaySelectOrganization: boolean = false;
    public displaySelectStudyType: boolean = false;
    public displaySelectYear: boolean = false;
    public displayMySpace: boolean = false;

    constructor(
        private readonly formBuilder: FormBuilder,
        private readonly store: Store,
        private readonly sidenavService: SidenavService,
        private readonly router: Router,
        private readonly organizationService: OrganizationService,
    ) {
        inject(BreakpointObserver)
            .observe(sidenavService.breakPointsThreshold)
            .pipe(untilDestroyed(this))
            .subscribe((result) => {
                const matches = result.matches;
                this.isSmallScreen = matches;
            });

        combineLatest([this.loggedUser$, this.router.events])
            .pipe(untilDestroyed(this))
            .subscribe(([loggedUser, event]) => {
                //Choose what to display on header:
                const currentUrl = this.router.url;
                //Normal User, My Space page
                if (!loggedUser.isAdmin && currentUrl == '/main') {
                    this.displayAdministration = false;
                    this.displayMySpace = true;
                    this.displaySelectOrganization = false;
                    this.displaySelectStudyType = false;
                    this.displaySelectYear = false;
                }
                //Admin on a study
                else if (
                    loggedUser.isAdmin &&
                    currentUrl.match(
                        /main\/[0-9A-Fa-f]{8}-[0-9A-Fa-f]{4}-[0-9A-Fa-f]{4}-[0-9A-Fa-f]{4}-[0-9A-Fa-f]{12}\/[AZa-z]+/,
                    )
                ) {
                    this.displayAdministration = false;
                    this.displayMySpace = false;
                    this.displaySelectOrganization = true;
                    this.displaySelectStudyType = true;
                    this.displaySelectYear = true;
                }
                //admin on My Account : hide everything
                else if (loggedUser.isAdmin && currentUrl == '/main/account') {
                    this.displayAdministration = true;
                    this.displayMySpace = false;
                    this.displaySelectOrganization = false;
                    this.displaySelectStudyType = false;
                    this.displaySelectYear = false;
                }
                //User on My Account : hide everything
                else if (!loggedUser.isAdmin && currentUrl == '/main/account') {
                    this.displayAdministration = false;
                    this.displayMySpace = true;
                    this.displaySelectOrganization = false;
                    this.displaySelectStudyType = false;
                    this.displaySelectYear = false;
                } //User on study
                else if (!loggedUser.isAdmin && currentUrl.includes('/main/')) {
                    this.displayAdministration = false;
                    this.displayMySpace = true;
                    this.displaySelectOrganization = false;
                    this.displaySelectStudyType = true;
                    this.displaySelectYear = true;
                }
                //Admin on My Space
                else if (loggedUser.isAdmin && currentUrl.includes('/main/')) {
                    this.displayAdministration = false;
                    this.displayMySpace = false;
                    this.displaySelectOrganization = true;
                    this.displaySelectStudyType = false;
                    this.displaySelectYear = false;
                }
                //Admin organizations Page
                else if (currentUrl == '/admin' || currentUrl == '/admin/organization') {
                    this.displayAdministration = true;
                    this.displayMySpace = false;
                    this.displaySelectOrganization = false;
                    this.displaySelectStudyType = false;
                    this.displaySelectYear = false;
                }
                //Admin on organization page
                else if (currentUrl.includes('/admin/')) {
                    this.displayAdministration = true;
                    this.displayMySpace = false;
                    this.displaySelectOrganization = true;
                    this.displaySelectStudyType = false;
                    this.displaySelectYear = false;
                }
            });
        //Update initials
        this.loggedUser$.pipe(untilDestroyed(this)).subscribe((loggedUser) => {
            this.initials =
                (loggedUser.firstname?.charAt(0) || '') + (loggedUser.lastname?.charAt(0) || '');
        });

        //update list choices for user :
        combineLatest([
            this.currentOrganization$,
            this.currentYear$,
            this.currentStudyType$,
            this.loggedUser$,
        ])
            .pipe(untilDestroyed(this), distinctUntilChanged())
            .subscribe(([currentOrganization, currentYear, currentStudyType, loggedUser]) => {
                if (currentOrganization) {
                    if (currentStudyType == StudyType.CRACMAP) {
                        this.getYearOptions(
                            currentOrganization.cracMaps,
                            loggedUser.isAdmin,
                            CracMapStatus.AVAILABLE,
                        );
                    }
                    //CC by default
                    else {
                        this.getYearOptions(
                            currentOrganization.concessionControls,
                            loggedUser.isAdmin,
                            ConcessionControlStatus.AVAILABLE,
                        );
                    }

                    this.headerFormGroup.patchValue({
                        organizationId: currentOrganization.id,
                        studyType: currentStudyType,
                        year: currentYear?.toString(),
                    });
                    this.subscribeOnInputChange();
                }
            });

        //For admin, update organizations list :
        this.organizations$.pipe(untilDestroyed(this)).subscribe((organizations) => {
            if (organizations) {
                this.organizationOptions = organizations
                    .map((organization) => ({
                        label: organization.name,
                        value: organization.id,
                    }))
                    .sort((a: OptionList, b: OptionList) =>
                        a.label.toLowerCase().localeCompare(b.label.toLowerCase()),
                    );
            }
        });

        //update Avatar :
        this.currentOrganization$.pipe(untilDestroyed(this)).subscribe((currentOrganization) => {
            if (currentOrganization?.id) {
                this.organizationService
                    .getAvatar(currentOrganization!.id!)
                    .pipe(
                        map((blob) => (!!blob ? URL.createObjectURL(blob) : null)),
                        untilDestroyed(this),
                    )
                    .subscribe((avatarSrc) => (this.avatarSrc = avatarSrc));
            } else {
                this.avatarSrc = '';
            }
        });
    }

    private subscribeOnInputChange() {
        combineLatest([
            this.headerFormGroup.valueChanges,
            this.currentOrganization$,
            this.currentStudyType$,
            this.currentYear$,
        ])
            .pipe(untilDestroyed(this), debounceTime(200), distinctUntilChanged())
            .subscribe(([formValues, currentOrganization, currentStudyType, currentYear]) => {
                //Reload on change
                let nextUrl = this.router.url
                    .replace(currentOrganization?.id ?? "'", formValues['organizationId'])
                    .replace(currentYear?.toString() ?? "'", formValues['year'])
                    .replace(currentStudyType ?? "'", formValues['studyType']);

                //If we change studytype; we need to go to default welcomepage /main/{uuid}/{studytype}/{year}
                if (currentStudyType != null && currentStudyType != formValues['studyType']) {
                    const index = nextUrl.indexOf(formValues['year']);
                    if (index > 0) {
                        nextUrl = `${nextUrl.substring(0, index)}${formValues['year']}/`;
                    }
                }

                if (nextUrl != this.router.url) {
                    this.router.navigate([nextUrl]);
                }
            });
    }

    public toggleSidenav() {
        this.sidenavService.toggle();
    }

    public logOut(): void {
        this.store.dispatch(new Logout());
    }

    private getYearOptions(
        studies: any[] | undefined | null,
        isAdmin: boolean,
        available: number,
    ): void {
        if (studies) {
            this.yearOptions = studies!
                .filter((study) => isAdmin || study.status == available)
                .map((study) => ({
                    label: study.year.toString(),
                    value: study.year.toString(),
                }));
            this.yearOptions.sort((a: OptionList, b: OptionList) => b.value.localeCompare(a.value));
        } else {
            this.yearOptions = [];
        }
    }
}
